import { useState } from 'react';
import {
  Plus, Eye, Copy, Check, RefreshCw, RotateCcw,
  FileSpreadsheet, FileText, FileUp, ListChecks, Scale, Clock, Banknote, X,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useContrePasser, useJournal, usePostJournalEntry } from '@/hooks/useJournal';
import { PeriodesBar } from '@/components/finance/PeriodesBar';
import { JournalEntryDialog } from '@/components/finance/JournalEntryDialog';
import { JOURNAL_TYPE_LABELS, STATUS_LABELS, type JournalEntry, type JournalType, type JournalEntryStatus } from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';
import { demander } from '@/components/ui/Fenetres';

/**
 * Journal Comptable pixel-perfect CTO-FRONT-002.
 * Référence : design-journal-comptable-v1.html
 */

const fmtDateShort = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')} ${['jan','fév','mar','avr','mai','juin','juil','août','sept','oct','nov','déc'][d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
};

const STATUS_BADGE: Record<JournalEntryStatus, string> = {
  draft:     'ds-bd ds-bd-n',
  posted:    'ds-bd ds-bd-s',
  cancelled: 'ds-bd ds-bd-d',
};

export const JournalPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [search, setSearch]           = useState('');
  const [journalType, setJournalType] = useState<JournalType | ''>('');
  const [status, setStatus]           = useState<JournalEntryStatus | ''>('');
  const [dateFrom, setDateFrom]       = useState('');
  const [dateTo, setDateTo]           = useState('');
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [postError, setPostError]     = useState<string | null>(null);

  const { data, isLoading, refetch } = useJournal({
    journalType: journalType || undefined,
    status:      status      || undefined,
    dateFrom:    dateFrom    || undefined,
    dateTo:      dateTo      || undefined,
    limit: 100,
  });
  const post = usePostJournalEntry();
  const contrePasser = useContrePasser();
  const role = useAuthStore((s) => s.user?.role);

  const entries = data?.data ?? [];
  const filtered = search
    ? entries.filter((e) =>
        e.reference.toLowerCase().includes(search.toLowerCase()) ||
        e.description.toLowerCase().includes(search.toLowerCase()) ||
        e.lines.some((l) => l.account?.code.includes(search) || l.account?.label.toLowerCase().includes(search.toLowerCase()))
      )
    : entries;

  // Flatten lines : 1 row per journal line (au lieu d'1 row par entry)
  const rows = filtered.flatMap((entry) =>
    entry.lines.map((line, idx) => ({
      entryId:     entry.id,
      entryDate:   entry.entryDate,
      reference:   entry.reference,
      numeroPiece: entry.numeroPiece ?? null,
      contrePassee: entries.some((e) => e.contrePasseId === entry.id),
      description: entry.description,
      journalType: entry.journalType,
      status:      entry.status,
      lineIndex:   idx,
      lineCount:   entry.lines.length,
      line,
      contact:     (entry as any).contact, // si on ajoute plus tard
    }))
  );

  // Stats
  const totalDebit  = rows.reduce((s, r) => s + Number(BigInt(r.line.debitCents)),  0);
  const totalCredit = rows.reduce((s, r) => s + Number(BigInt(r.line.creditCents)), 0);
  const draftCount  = entries.filter((e) => e.status === 'draft').length;
  const balanced    = Math.abs(totalDebit - totalCredit) < 1;

  // Lot 2, étape 2.3 : une écriture validée ne se modifie pas — elle se corrige par une écriture inverse.
  const handleContrePasser = async (id: string, piece: string) => {
    setPostError(null);
    const motif = await demander.texte({
      titre: `Contre-passer ${piece}`, message: 'Une écriture inverse, validée, va être créée.',
      libelle: 'Motif de la correction (obligatoire)', min: 5, multiligne: true, confirmer: 'Contre-passer', danger: true,
    });
    if (motif === null) return;
    try { await contrePasser.mutateAsync({ id, motif }); } catch (err) { setPostError(extractError(err).message); }
  };

  const handlePost = async (id: string, ref: string) => {
    setPostError(null);
    if (!(await demander.confirmer({ titre: `Valider l'écriture ${ref} ?`, message: 'Une écriture validée ne se modifie plus ; elle se corrige par contre-passation.', confirmer: 'Valider' }))) return;
    try { await post.mutateAsync(id); } catch (err) { setPostError(extractError(err).message); }
  };

  const activeFilters: { label: string; clear: () => void }[] = [];
  if (status)      activeFilters.push({ label: `Statut: ${STATUS_LABELS[status]}`, clear: () => setStatus('') });
  if (journalType) activeFilters.push({ label: `Journal: ${journalType}`,           clear: () => setJournalType('') });
  if (dateFrom)    activeFilters.push({ label: `Depuis: ${dateFrom}`,               clear: () => setDateFrom('') });
  if (dateTo)      activeFilters.push({ label: `Jusqu'à: ${dateTo}`,                clear: () => setDateTo('') });

  const clearAll = () => {
    setSearch(''); setJournalType(''); setStatus(''); setDateFrom(''); setDateTo('');
  };

  // ── Export Excel / PDF ─────────────────────────────────────
  const exportColumns: ExportColumn<JournalEntry>[] = [
    { header: 'Date',        accessor: (e) => fmtDateShort(e.entryDate) },
    { header: 'N° pièce',    accessor: (e) => e.numeroPiece ?? 'brouillon' },
    { header: 'Référence',   accessor: (e) => e.reference },
    { header: 'Journal',     accessor: (e) => JOURNAL_TYPE_LABELS[e.journalType] },
    { header: 'Description', accessor: (e) => e.description },
    { header: 'Statut',      accessor: (e) => STATUS_LABELS[e.status] },
    {
      header: 'Total débit',
      accessor: (e) => e.lines.reduce((s, l) => s + Number(BigInt(l.debitCents)), 0) / 100,
      align: 'right',
    },
    {
      header: 'Total crédit',
      accessor: (e) => e.lines.reduce((s, l) => s + Number(BigInt(l.creditCents)), 0) / 100,
      align: 'right',
    },
  ];

  const exportData = filtered;
  const filialeKey = filiale?.filialeKey;

  const onExportXLSX = () => exportToXLSX({
    filename: buildExportFilename('Journal', filialeKey, 'xlsx'),
    sheetName: 'Journal SYSCOHADA',
    columns: exportColumns,
    rows: exportData,
  });

  const onExportPDF = () => exportToPDF({
    title:    `Journal Comptable — ${filiale?.fullName ?? ''}`,
    subtitle: `SYSCOHADA révisé · Exercice ${new Date().getFullYear()} · ${exportData.length} écriture${exportData.length > 1 ? 's' : ''}`,
    filename: buildExportFilename('Journal', filialeKey, 'pdf'),
    orientation: 'landscape',
    columns: exportColumns,
    rows: exportData,
  });

  return (
    <>
      <Topbar
        title="Journal Comptable"
        subtitle={filiale ? `SYSCOHADA révisé · ${filiale.fullName} · Exercice ${new Date().getFullYear()}` : 'SYSCOHADA'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" disabled title="Bientôt"><FileUp size={14} /> Importer CSV</button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportXLSX()} disabled={exportData.length === 0} title="Exporter Excel">
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void onExportPDF()} disabled={exportData.length === 0} title="Exporter PDF">
              <FileText size={14} /> PDF
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouvelle écriture
            </button>
          </>
        }
      />

      <div className="ds-content">
        <PeriodesBar />
        {postError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico">⚠</span>
            <div className="ds-alert-txt">{postError}</div>
          </div>
        )}

        {/* Stats bar */}
        <div className="ds-stats-bar">
          <div className="ds-stat-card">
            <div className="ds-stat-ico" style={{ background: '#d1fae5' }}><ListChecks size={18} /></div>
            <div>
              <div className="ds-stat-val">{entries.length}</div>
              <div className="ds-stat-lbl">Écritures sur la période</div>
            </div>
          </div>
          <div className="ds-stat-card">
            <div className="ds-stat-ico" style={{ background: '#dbeafe' }}><Scale size={18} /></div>
            <div>
              <div className="ds-stat-val" style={{ fontSize: 14 }}>{formatNumber(totalDebit / 100)}</div>
              <div className="ds-stat-lbl">Total débit · FCFA {balanced && entries.length > 0 ? '✓' : ''}</div>
            </div>
          </div>
          <div className="ds-stat-card">
            <div className="ds-stat-ico" style={{ background: '#fef3c7' }}><Clock size={18} /></div>
            <div>
              <div className="ds-stat-val">{draftCount}</div>
              <div className="ds-stat-lbl">Brouillons en attente</div>
            </div>
          </div>
          <div className="ds-stat-card">
            <div className="ds-stat-ico" style={{ background: 'var(--p-soft)' }}><Banknote size={18} /></div>
            <div>
              <div className="ds-stat-val">{new Set(entries.map((e) => e.journalType)).size}</div>
              <div className="ds-stat-lbl">Journaux actifs</div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="ds-filters-panel">
          <div className="ds-filters-row">
            <div className="ds-sw">
              <input
                className="ds-fi ds-fi-search"
                type="search"
                placeholder="Rechercher libellé, référence, compte..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <input className="ds-fi ds-fi-date" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span className="ds-filter-sep">→</span>
            <input className="ds-fi ds-fi-date" type="date" value={dateTo}   onChange={(e) => setDateTo(e.target.value)} />
            <select
              className="ds-fi ds-fi-sel"
              value={status}
              onChange={(e) => setStatus(e.target.value as JournalEntryStatus | '')}
            >
              <option value="">Tous les statuts</option>
              <option value="draft">📝 Brouillon</option>
              <option value="posted">✅ Validé</option>
              <option value="cancelled">❌ Annulé</option>
            </select>
            <select
              className="ds-fi ds-fi-sel"
              value={journalType}
              onChange={(e) => setJournalType(e.target.value as JournalType | '')}
            >
              <option value="">Tous les journaux</option>
              {(Object.keys(JOURNAL_TYPE_LABELS) as JournalType[]).map((t) => (
                <option key={t} value={t}>{t} — {JOURNAL_TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>
          {activeFilters.length > 0 && (
            <div className="ds-active-filters">
              {activeFilters.map((f, i) => (
                <span key={i} className="ds-filter-tag">
                  {f.label}
                  <button type="button" onClick={f.clear} aria-label={`Effacer ${f.label}`}><X size={11} /></button>
                </span>
              ))}
              <button type="button" onClick={clearAll} style={{ fontSize: 12, color: 'var(--tx-3)', padding: '3px 8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                Effacer tout
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Écritures de la période</div>
              <div className="ds-table-sub">{rows.length} ligne{rows.length > 1 ? 's' : ''} · {entries.length} écriture{entries.length > 1 ? 's' : ''}</div>
            </div>
          </div>

          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Date</th>
                <th style={{ width: 120 }}>Référence</th>
                <th>Libellé</th>
                <th style={{ width: 140 }}>Compte</th>
                <th className="ds-am" style={{ width: 140 }}>Débit</th>
                <th className="ds-am" style={{ width: 140 }}>Crédit</th>
                <th style={{ width: 100 }}>Statut</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune écriture sur la période</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={() => setDialogOpen(true)}>
                      <Plus size={12} /> Créer la première écriture
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && rows.map((r, i) => {
                const debit  = Number(BigInt(r.line.debitCents));
                const credit = Number(BigInt(r.line.creditCents));
                const isFirstLine = r.lineIndex === 0;
                return (
                  <tr key={`${r.entryId}-${i}`} style={r.status === 'draft' ? { background: 'rgba(245,158,11,.03)' } : undefined}>
                    <td style={{ color: 'var(--tx-3)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {isFirstLine ? fmtDateShort(r.entryDate) : ''}
                    </td>
                    <td>{isFirstLine && (
                      <>
                        <span className="ds-ref-code">{r.numeroPiece ?? 'Brouillon'}</span>
                        <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{r.reference}</div>
                      </>
                    )}</td>
                    <td>
                      <div className="ds-entry-label">{r.line.label}</div>
                      {isFirstLine && r.description && <div className="ds-entry-sub">{r.description}</div>}
                    </td>
                    <td>
                      <div className="ds-compte-code">{r.line.account?.code ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{r.line.account?.label}</div>
                    </td>
                    <td className={`ds-am ${debit > 0 ? 'ds-amp' : ''}`} style={debit === 0 ? { color: 'var(--tx-3)' } : undefined}>
                      {debit > 0 ? formatNumber(debit / 100) : '—'}
                    </td>
                    <td className="ds-am" style={credit > 0 ? { color: 'var(--danger)' } : { color: 'var(--tx-3)' }}>
                      {credit > 0 ? formatNumber(credit / 100) : '—'}
                    </td>
                    <td>
                      {isFirstLine && (
                        <span className={STATUS_BADGE[r.status]}>{STATUS_LABELS[r.status]}</span>
                      )}
                    </td>
                    <td>
                      {isFirstLine && (
                        <div className="ds-row-actions">
                          <button className="ds-row-btn" title="Voir détail"><Eye size={13} /></button>
                          <button className="ds-row-btn" title="Dupliquer"><Copy size={13} /></button>
                          {r.status === 'posted' && role === 'daf' && !r.contrePassee && (
                            <button
                              className="ds-row-btn"
                              title="Contre-passer (corriger par une écriture inverse)"
                              onClick={() => void handleContrePasser(r.entryId, r.numeroPiece ?? r.reference)}
                              disabled={contrePasser.isPending}
                            >
                              <RotateCcw size={13} />
                            </button>
                          )}
                          {r.status === 'draft' && (
                            <button
                              className="ds-row-btn"
                              title="Valider"
                              style={{ color: 'var(--success)' }}
                              onClick={() => handlePost(r.entryId, r.reference)}
                              disabled={post.isPending}
                            >
                              <Check size={13} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ fontSize: 12, opacity: 0.9 }}>
                    Totaux · {entries.length} écriture{entries.length > 1 ? 's' : ''} · {balanced ? 'Équilibre vérifié' : '⚠ Déséquilibre'}
                  </td>
                  <td className="ds-am">{formatNumber(totalDebit / 100)}</td>
                  <td className="ds-am">{formatNumber(totalCredit / 100)}</td>
                  <td style={{ textAlign: 'center' }}>{balanced ? '✅' : '⚠'}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>

          {data && data.meta.total > 0 && (
            <div className="ds-pagination">
              <span className="ds-pg-info">
                Affichage <strong>1 – {Math.min(data.meta.limit, data.meta.total)}</strong> sur <strong>{data.meta.total}</strong> écriture{data.meta.total > 1 ? 's' : ''}
              </span>
              {data.meta.pages > 1 && (
                <div className="ds-pg-pages">
                  <button className="ds-pg-btn" disabled={data.meta.page === 1}>‹</button>
                  {Array.from({ length: Math.min(5, data.meta.pages) }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`ds-pg-btn${p === data.meta.page ? ' on' : ''}`}>{p}</button>
                  ))}
                  <button className="ds-pg-btn" disabled={data.meta.page === data.meta.pages}>›</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <JournalEntryDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};
